"use client"

import { useState, useEffect } from "react"
import { getAuth } from "firebase/auth"
import { getDatabase, ref as dbRef, get } from "firebase/database"
import { getStorage, ref as storageRef, getDownloadURL } from "firebase/storage"
import { useNavigate, Link } from "react-router-dom"
import { FaCheck, FaShareAlt, FaFileAlt, FaBuilding, FaMapMarkerAlt } from "react-icons/fa"
import { MdInfo } from "react-icons/md"
import "./DocumentsUploadPage.css"
import { useAccount } from "../context/AccountContext"

const DocumentsPage = () => {
  const navigate = useNavigate()
  const { currentAccount, loading: accountLoading } = useAccount()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [sitesData, setSitesData] = useState([])
  const [selectedSite, setSelectedSite] = useState(null)

  const database = getDatabase()
  const storage = getStorage()

  const normalizeString = (str) => {
    if (!str) return ""
    return str.toString().toLowerCase().trim().replace(/\s+/g, " ")
  }

  const docLabels = {
    gstCopy: "GST Copy",
    panCard: "PAN Card",
    workOrder: "Work Order",
    letterHead: "Letter Head",
    tanNumber: "TAN Number",
    cinCertificate: "CIN Certificate",
    partnershipDeed: "Partnership Deed",
    purchaseOrder: "Purchase Order (Optional)",
    cancelCheque: "Cancel Cheque (Optional)",
    TDS: "TDS Certificate",
    TCS: "TCS Certificate",
  }

  // Generate fresh download URL from storage path
  const generateDownloadUrl = async (storagePath) => {
    if (!storagePath) return null
    try {
      const fileRef = storageRef(storage, storagePath)
      return await getDownloadURL(fileRef)
    } catch (error) {
      console.error("Error generating download URL:", error)
      return null
    }
  }

  // Open document in new tab
  const openDocument = async (storagePath) => {
    const url = await generateDownloadUrl(storagePath)
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer")
    } else {
      alert("Unable to open document. Please try again.")
    }
  }

  // Share document
  const handleShare = async (storagePath, label) => {
    const url = await generateDownloadUrl(storagePath)
    if (!url) {
      alert("Unable to generate shareable link.")
      return
    }

    try {
      if (navigator.share) {
        await navigator.share({
          title: `${label} Document`,
          text: `My ${label}`,
          url: url,
        })
      } else {
        await navigator.clipboard.writeText(url)
        alert("Document link copied to clipboard!")
      }
    } catch (err) {
      await navigator.clipboard.writeText(url)
      alert("Link copied to clipboard!")
    }
  }

  // Fetch documents when currentAccount changes
  useEffect(() => {
    if (accountLoading) {
      setLoading(true)
      return
    }

    if (!currentAccount?.uid) {
      setError("No account selected.")
      setLoading(false)
      return
    }

    const fetchAllUserDocuments = async (uid) => {
      setLoading(true)
      setError("")

      try {
        const snapshot = await get(dbRef(database, `userDocs/${uid}`))
        if (!snapshot.exists()) {
          setSitesData([])
          setSelectedSite(null)
          setLoading(false)
          return
        }

        const data = snapshot.val()
        const allSites = []

        for (const [brand, brandData] of Object.entries(data)) {
          for (const [siteKey, docs] of Object.entries(brandData)) {
            const parts = siteKey.split("_")
            const pincode = parts.pop() || ""
            const state = parts.pop() || ""
            const address = parts.join("_").replace(/_/g, " ")

            // Filter out empty/null paths
            const validDocs = Object.entries(docs)
              .filter(([_, path]) => path && typeof path === "string")
              .reduce((acc, [type, path]) => {
                acc[type] = path  // Store path string
                return acc
              }, {})

            const siteObj = {
              id: `${brand}_${siteKey}`,
              brand,
              siteKey,
              address,
              state,
              pincode: pincode || null,
              documents: validDocs,
              hasDocs: Object.keys(validDocs).length > 0
            }

            allSites.push(siteObj)
          }
        }

        allSites.sort((a, b) => a.brand.localeCompare(b.brand) || a.address.localeCompare(b.address))

        setSitesData(allSites)

        const firstWithDocs = allSites.find(s => s.hasDocs)
        setSelectedSite(firstWithDocs || allSites[0] || null)

      } catch (err) {
        console.error("Error fetching documents:", err)
        setError("Failed to load documents.")
      } finally {
        setLoading(false)
      }
    }

    fetchAllUserDocuments(currentAccount.uid)
  }, [currentAccount, accountLoading])

  const docsInfo = () => {
    alert("Your uploaded documents across all brands and delivery sites for the selected account. Select a site from the left to view its documents.")
  }

  if (accountLoading || loading) {
    return <div className="new-loading-container">Loading documents...</div>
  }

  if (error) {
    return <div className="doc-error">{error}</div>
  }

  return (
    <div className="doc-page-wrapper">
      <div className="doc-header">
        <h1 className="doc-title">
          My Documents <MdInfo onClick={docsInfo} className="icon-i mt-2" />
        </h1>
        <p className="doc-subtitle">
          Viewing documents for: <strong>{currentAccount?.company_name || "Account"}</strong>
        </p>
      </div>

      {sitesData.length === 0 ? (
        <div className="doc-empty">
          <FaFileAlt className="doc-empty-icon" />
          <h3>No Documents Yet</h3>
          <p>You haven't uploaded any documents for this account.</p>
          <Link to="/dashboard/product-order" className="doc-cta-btn">
            Place Order to Upload
          </Link>
        </div>
      ) : (
        <div className="doc-layout">
          {/* Sidebar */}
          <div className="Doc-sidebar">
            <h3 className="doc-sidebar-title">Your Sites ({sitesData.length})</h3>
            <div className="doc-sites-list">
              {sitesData.map((site) => (
                <div
                  key={site.id}
                  className={`doc-site-item ${selectedSite?.id === site.id ? "active" : ""}`}
                  onClick={() => setSelectedSite(site)}
                >
                  <div className="doc-site-brand">
                    <FaBuilding /> {site.brand}
                  </div>
                  <div className="doc-site-address">
                    <FaMapMarkerAlt /> {site.address}
                    {site.state && `, ${site.state}`}
                    {site.pincode && ` - ${site.pincode}`}
                  </div>
                  <div className="doc-site-status">
                    {site.hasDocs ? (
                      <span className="doc-has-docs"><FaCheck /> Documents Uploaded</span>
                    ) : (
                      <span className="doc-no-docs">No documents</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Main Content */}
          <div className="doc-main">
            {selectedSite ? (
              <>
                <div className="doc-selected-header">
                  <h2>{selectedSite.brand}</h2>
                  <p className="doc-selected-address">
                    {selectedSite.address}
                    {selectedSite.state && `, ${selectedSite.state}`}
                    {selectedSite.pincode && ` - ${selectedSite.pincode}`}
                  </p>
                </div>

                {selectedSite.hasDocs ? (
                  <div className="doc-docs-grid">
                    {Object.keys(docLabels).map((docType) => {
                      const hasDoc = !!selectedSite.documents[docType]
                      const label = docLabels[docType]
                      const storagePath = selectedSite.documents[docType]

                      return (
                        <div key={docType} className="doc-doc-card">
                          <div className="doc-doc-title">
                            <span className={`doc-status-icon ${hasDoc ? "uploaded" : "missing"}`}>
                              {hasDoc ? <FaCheck /> : "—"}
                            </span>
                            {label}
                          </div>
                          {hasDoc ? (
                            <div className="doc-doc-actions">
                              <button
                                onClick={() => openDocument(storagePath)}
                                className="doc-view-btn"
                              >
                                View
                              </button>
                              <button
                                onClick={() => handleShare(storagePath, label)}
                                className="doc-share-btn"
                                title="Share"
                              >
                                <FaShareAlt />
                              </button>
                            </div>
                          ) : (
                            <span className="doc-not-uploaded">Not uploaded</span>
                          )}
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="doc-no-docs-selected">
                    <FaFileAlt className="doc-big-icon" />
                    <p>No documents uploaded for this site yet.</p>
                  </div>
                )}
              </>
            ) : (
              <div className="doc-select-prompt">
                <FaFileAlt className="doc-big-icon" />
                <h3>Select a site from the left</h3>
                <p>to view its uploaded documents</p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="doc-footer-note">
        <p>
          <strong>Privacy:</strong> Documents are securely stored and used only for order verification.{" "}
          <Link to="/privacy-policy" className="text-primary">Privacy Policy</Link>
        </p>
      </div>
    </div>
  )
}

export default DocumentsPage